<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-back-button defaultHref="/" [text]="'COMMON.BACK' | translate" />
    </ion-buttons>
    <ion-title data-cy="create-product-title">{{ "PRODUCTS.CREATE_PRODUCT" | translate }}</ion-title>
    <ion-buttons slot="end">
      <mtjr-submit-button
        fill="clear"
        data-cy="product-submit-button"
        [label]="'COMMON.CREATE' | translate"
        (click)="submit()"
        [state]="store.productCreateState()"
      />
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content>
  <div class="flex justify-center pt-4">
    <div class="w-full max-w-[640px] lg:p-3">
      <form [formGroup]="createUtils.form" (ngSubmit)="submit()">
        <div class="flex w-full flex-col">
          <div class="bg-surface mb-2 w-full rounded-md px-5 py-3">
            <mtjr-error-bar [failure]="store.productCreateFailure()" />
            <div class="mx-1 mb-2">
              <ion-text class="text-sm">{{ "PRODUCTS.PRODUCT_IMAGES" | translate }}</ion-text>
            </div>
            <mtjr-attachment-field [field]="createUtils.form.controls.images" />
          </div>
          <div class="mb-1 mt-2 flex-1">
            <fieldset class="bg-surface rounded-md px-4 py-3">
              <mtjr-text-field
                data-cy="product-name-input"
                [field]="createUtils.form.controls.name"
                label="{{ 'PRODUCTS.PRODUCT_NAME' | translate }}"
                class="w-full"
              />
              <div class="my-4"></div>
              <mtjr-text-area-field
                data-cy="product-description-input"
                [field]="createUtils.form.controls.description"
                label="{{ 'PRODUCTS.DESCRIPTION' | translate }}"
                class="mb-2 w-full"
              />
              <div class="my-4"></div>

              @if (attributesStore.variants().length === 0) {
                <div class="flex">
                  <mtjr-text-field
                    data-cy="product-price-input"
                    [field]="createUtils.form.controls.price"
                    label="{{ 'PRODUCTS.PRICE' | translate }}"
                    type="number"
                    class="w-full"
                  />
                  <div class="mx-1"></div>
                  <mtjr-text-field
                    data-cy="product-discount-input"
                    [field]="createUtils.form.controls.discount"
                    label="{{ 'PRODUCTS.DISCOUNT' | translate }}"
                    type="number"
                    class="w-full"
                  />
                </div>
                <div class="my-4"></div>
                <mtjr-text-field
                  [field]="createUtils.form.controls.stock"
                  data-cy="product-quantity-input"
                  type="button"
                  label="{{ 'PRODUCTS.PRODUCT_QUANTITY' | translate }}"
                  type="number"
                  class="mb-2 w-full"
                />
              }
              <!-- Product Size Selection -->
              <div>
                <ion-text class="text-sm font-medium block py-2">{{ "PRODUCTS.SIZE" | translate }} *</ion-text>
                <ion-radio-group [formControlName]="'size'" class="flex flex-col gap-1">
                  <ion-item lines="none" class="bg-surface-variant rounded-lg">
                    <ion-radio slot="start" value="small" class="mr-3 opposite-dir">
                      <ion-label class="flex flex-col force-html-dir">
                        <div class="font-medium text-base">{{ "PRODUCTS.SIZE_SMALL" | translate }}</div>
                        <div class="text-sm text-medium mt-1">{{ "PRODUCTS.SIZE_SMALL_DESCRIPTION" | translate }}</div>
                        <div class="text-xs text-medium mt-1">{{ "PRODUCTS.SIZE_SMALL_EXAMPLES" | translate }}</div>
                      </ion-label>
                    </ion-radio>
                  </ion-item>

                  <ion-item lines="none" class="bg-surface-variant rounded-lg">
                    <ion-radio slot="start" value="medium" class="mr-3 opposite-dir">
                      <ion-label class="flex flex-col force-html-dir">
                        <div class="font-medium text-base">{{ "PRODUCTS.SIZE_MEDIUM" | translate }}</div>
                        <div class="text-sm text-medium mt-1">{{ "PRODUCTS.SIZE_MEDIUM_DESCRIPTION" | translate }}</div>
                        <div class="text-xs text-medium mt-1">{{ "PRODUCTS.SIZE_MEDIUM_EXAMPLES" | translate }}</div>
                      </ion-label>
                    </ion-radio>
                  </ion-item>

                  <ion-item lines="none" class="bg-surface-variant rounded-lg">
                    <ion-radio slot="start" value="large" class="mr-3 opposite-dir">
                      <ion-label class="flex flex-col force-html-dir">
                        <div class="font-medium text-base">{{ "PRODUCTS.SIZE_LARGE" | translate }}</div>
                        <div class="text-sm text-medium mt-1">{{ "PRODUCTS.SIZE_LARGE_DESCRIPTION" | translate }}</div>
                        <div class="text-xs text-medium mt-1">{{ "PRODUCTS.SIZE_LARGE_EXAMPLES" | translate }}</div>
                      </ion-label>
                    </ion-radio>
                  </ion-item>

                  <ion-item lines="none" class="bg-surface-variant rounded-lg">
                    <ion-radio slot="start" value="overSize" class="mr-3 opposite-dir">
                      <ion-label class="flex flex-col force-html-dir">
                        <div class="font-medium text-base">{{ "PRODUCTS.SIZE_OVERSIZED" | translate }}</div>
                        <div class="text-sm text-medium mt-1">
                          {{ "PRODUCTS.SIZE_OVERSIZED_DESCRIPTION" | translate }}
                        </div>
                        <div class="text-xs text-medium mt-1">{{ "PRODUCTS.SIZE_OVERSIZED_EXAMPLES" | translate }}</div>
                      </ion-label>
                    </ion-radio>
                  </ion-item>
                </ion-radio-group>
              </div>
            </fieldset>

            <div class="bg-surface my-2 py-2 px-5 rounded-md">
              <ion-item lines="none">
                <ion-label>
                  <div class="flex flex-col">
                    <span>
                      {{ "PRODUCTS.ADVANCED_MODE" | translate }}
                    </span>
                    <span class="text-medium text-sm mt-1">
                      {{ "PRODUCTS.ADVANCED_MODE_DESCRIPTION" | translate }}
                    </span>
                  </div>
                </ion-label>
                <ion-toggle
                  slot="end"
                  data-cy="advanced-mode-toggle"
                  [checked]="advancedMode()"
                  [style]="{ '--background': 'var(--ion-color-surface)' }"
                  (ionChange)="advancedMode.set($event.detail.checked)"
                />
              </ion-item>
            </div>
            @if (advancedMode()) {
              @if (attributesStore.selectedAttributes().length > 0) {
                <div class="bg-surface mt-2 py-2 px-5 rounded-md">
                  <div class="flex items-center ps-2">
                    <ion-label class="flex-1">{{ "PRODUCTS.OPTIONS" | translate }}</ion-label>
                    <ion-button (click)="updateAttributes()" fill="clear">{{ "COMMON.EDIT" | translate }}</ion-button>
                  </div>
                  @for (attr of attributesStore.selectedAttributes(); track $index) {
                    <div class="mt-2">
                      <ion-text color="medium" class="mx-2 text-md">
                        {{ "ATTRIBUTE." + attr.label.toUpperCase() | translate }}
                      </ion-text>
                      <div class="flex mt-2 flex-wrap">
                        @for (value of attr.values; track $index) {
                          <ion-chip outline="true" (click)="this.attributesStore.toggleAttribute(attr, value)">
                            @if (attr.type === "color") {
                              <div class="size-5 rounded-full" [ngStyle]="{ 'background-color': value.key }"></div>
                            } @else {
                              <span>{{ value.label }}</span>
                            }
                            <ion-icon color="danger" name="close"></ion-icon>
                          </ion-chip>
                        }
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <ion-item
                  data-cy="attributes-update-trigger"
                  lines="none"
                  color="none"
                  detail="true"
                  class="bg-surface my-2 px-2 py-1 rounded-md cursor-pointer hover:bg-medium/20"
                  (click)="updateAttributes()"
                >
                  <ion-label>{{ "PRODUCTS.ADD_OPTIONS" | translate }}</ion-label>
                  <ion-icon slot="start" size="small" name="add-circle-outline"></ion-icon>
                </ion-item>
              }
            }

            @if (attributesStore.variants().length > 0) {
              <ion-item
                data-cy="variants-editor-trigger"
                lines="none"
                color="none"
                class="bg-surface px-4 my-2 py-1 rounded-md cursor-pointer hover:bg-medium/20"
                detail="true"
                (click)="showVariants()"
              >
                <ion-label>
                  <span>{{ "PRODUCTS.VARIANTS" | translate }}</span>
                </ion-label>
                <ion-label slot="end">( {{ attributesStore.variants().length }} )</ion-label>
              </ion-item>
            }
          </div>
        </div>
      </form>
    </div>
  </div>
</ion-content>
